import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { AlertTriangle, TrendingDown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuestionAnalytics = ({ qrId }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuestionAnalytics();
  }, [qrId]);

  const fetchQuestionAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/qr-codes/${qrId}/question-analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load question analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 border border-[#E2E8F0]">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#1d4ed8]" />
        </div>
      </Card>
    );
  }

  if (!data || !data.questions || data.questions.length === 0) {
    return (
      <Card className="p-6 border border-[#E2E8F0]" data-testid="question-analytics-empty">
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Per-Question Analytics
        </h2>
        <p className="text-[#475569] text-sm">No question-level data available yet. Data will appear after sessions interact with the form.</p>
      </Card>
    );
  }

  const funnelData = data.questions.map((q, i) => ({
    name: `Q${i + 1}`,
    fullName: q.question_text.length > 40 ? q.question_text.slice(0, 40) + '...' : q.question_text,
    reached: q.reached,
    answered: q.answered,
    dropOff: q.drop_off_rate,
    completion: q.completion_rate,
  }));

  const getBarColor = (dropOff) => {
    if (dropOff > 30) return '#ef4444';
    if (dropOff > 15) return '#f59e0b';
    return '#22c55e';
  };

  const highDropOff = data.questions.filter((q) => q.drop_off_rate > 20);

  return (
    <div className="space-y-6" data-testid="question-analytics">
      {/* Funnel Chart */}
      <Card className="p-6 border border-[#E2E8F0]">
        <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Question Funnel
        </h2>
        <p className="text-sm text-[#475569] mb-4">
          {data.total_sessions} sessions | {data.total_completed} completed
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={funnelData} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" stroke="#475569" />
            <YAxis dataKey="name" type="category" stroke="#475569" width={40} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 shadow-lg text-sm">
                    <p className="font-medium mb-1">{d.fullName}</p>
                    <p>Reached: {d.reached}</p>
                    <p>Answered: {d.answered}</p>
                    <p>Drop-off: {d.dropOff}%</p>
                  </div>
                );
              }}
            />
            <Bar dataKey="answered" name="Answered" radius={[0, 4, 4, 0]}>
              {funnelData.map((entry, i) => (
                <Cell key={`item-${i}`} fill={getBarColor(entry.dropOff)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Drop-off Rates Table */}
      <Card className="p-6 border border-[#E2E8F0]">
        <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Drop-off Rates
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dropoff-table">
            <thead>
              <tr className="border-b border-[#E2E8F0]">
                <th className="text-left py-2 px-3 font-medium text-[#475569]">#</th>
                <th className="text-left py-2 px-3 font-medium text-[#475569]">Question</th>
                <th className="text-center py-2 px-3 font-medium text-[#475569]">Reached</th>
                <th className="text-center py-2 px-3 font-medium text-[#475569]">Answered</th>
                <th className="text-center py-2 px-3 font-medium text-[#475569]">Drop-off</th>
              </tr>
            </thead>
            <tbody>
              {data.questions.map((q, i) => (
                <tr key={q.question_id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                  <td className="py-2 px-3 text-[#475569]">{i + 1}</td>
                  <td className="py-2 px-3 max-w-xs truncate">{q.question_text}</td>
                  <td className="py-2 px-3 text-center">{q.reached}</td>
                  <td className="py-2 px-3 text-center">{q.answered}</td>
                  <td className="py-2 px-3 text-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        q.drop_off_rate > 30
                          ? 'bg-red-100 text-red-700'
                          : q.drop_off_rate > 15
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {q.drop_off_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* High Drop-off Alerts */}
      {highDropOff.length > 0 && (
        <Card className="p-4 border border-amber-200 bg-amber-50" data-testid="dropoff-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h3 className="font-bold text-amber-800">High Drop-off Questions</h3>
          </div>
          <ul className="space-y-1">
            {highDropOff.map((q) => (
              <li key={q.question_id} className="flex items-center gap-2 text-sm text-amber-700">
                <TrendingDown className="h-3 w-3" />
                <span className="font-medium">{q.question_text}</span>
                <span className="text-xs">({q.drop_off_rate}% drop-off)</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};
